import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { getPasswordStrength } from './utils/passwordUtils';
import { useAppSelector } from 'app/hooks';
import { Controller, useForm } from 'react-hook-form';
import { useEffect } from 'react';
import InfoPopup from 'components/InfoPopup';

import { useUpdateUser } from 'features/userManagement/api/updateUser';
import { useUpdateUserPassword } from 'features/userManagement/api/updateUserPassword';
import { useSystemModules } from 'features/systemModules/api/getSystemModules';

const UserSettingsPage = () => {
  const updateUserMutation = useUpdateUser({});
  const updateUserPasswordMutation = useUpdateUserPassword({});

  const { data: modules } = useSystemModules({});

  const user = useAppSelector((state) => state.auth.user);
  const {
    control,
    handleSubmit,
    getValues,
    watch,
    reset,
    setError,
    formState: { errors, isSubmitted, isDirty },
  } = useForm();

  useEffect(() => {
    reset({
      useImperial: user?.us_customary_units ? 'imperial' : 'metric',
      timezone: user?.timezone === 'UTC' ? 'utc' : 'local',
      twenty_four_hour_time: user?.twenty_four_hour_time ? '24' : '12',
      date_format: user?.date_format ? user.date_format : 'M/d/yyyy',
      alarm_popups: user?.alarm_popups ? true : false,
    });
  }, [
    user?.us_customary_units,
    reset,
    user?.timezone,
    user?.twenty_four_hour_time,
    user?.date_format,
    user?.alarm_popups,
  ]);

  const onSubmit = async () => {
    try {
      const formState = getValues();
      if (formState.currentPassword && formState.newPassword)
        await updateUserPasswordMutation.mutateAsync({
          data: {
            currentPassword: formState.currentPassword,
            newPassword: formState.newPassword,
          },
        });
      if (user)
        await updateUserMutation.mutateAsync({
          data: {
            ...formState,
            us_customary_units:
              formState.useImperial === 'imperial' ? true : false,
            timezone: formState.timezone === 'utc' ? 'UTC' : 'Local',
            twenty_four_hour_time:
              formState.twenty_four_hour_time === '24' ? true : false,
          },
          userId: user.id,
        });
      reset({ newPassword: '', currentPassword: '' }, { keepValues: true });
    } catch (error: any) {
      setError('newPassword', {
        type: 'newPassword',
        message: 'Old password incorrect',
      });
    }
  };

  return (
    <Grid container>
      <Grid
        container
        item
        style={{ margin: 0 }}
        component={Paper}
        spacing={2}
        xs={12}
        md={7}
        lg={5}
      >
        {modules?.WEATHER && (
          <Grid item container alignItems='center' spacing={1}>
            <Grid item>
              <Typography>Units for Weather:</Typography>
            </Grid>
            <Controller
              name='useImperial'
              control={control}
              defaultValue=''
              render={({ field: { onChange, value } }) => (
                <Grid item>
                  <ToggleButtonGroup
                    exclusive
                    onChange={onChange}
                    value={value}
                  >
                    <ToggleButton value='metric'>Metric</ToggleButton>
                    <ToggleButton value='imperial'>US Standard</ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              )}
            />
          </Grid>
        )}
        <Grid item container alignItems='center' spacing={1}>
          <Grid item>
            <Typography>Timezone:</Typography>
          </Grid>
          <Controller
            name='timezone'
            control={control}
            defaultValue=''
            render={({ field: { onChange, value } }) => (
              <Grid item>
                <ToggleButtonGroup exclusive onChange={onChange} value={value}>
                  <ToggleButton value='local'>Local</ToggleButton>
                  <ToggleButton value='utc'>UTC</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            )}
          />
          <Grid item>
            <Typography>Clock:</Typography>
          </Grid>
          <Controller
            name='twenty_four_hour_time'
            control={control}
            defaultValue=''
            render={({ field: { onChange, value } }) => (
              <Grid item>
                <ToggleButtonGroup exclusive onChange={onChange} value={value}>
                  <ToggleButton value='12'>12-Hour</ToggleButton>
                  <ToggleButton value='24'>24-Hour</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            )}
          />
        </Grid>
        <Controller
          name='date_format'
          control={control}
          defaultValue=''
          rules={{ required: false }}
          render={({ field: { onChange, value } }) => (
            <Grid item xs={12} style={{ marginRight: '1.2rem' }}>
              <FormControl variant='outlined' style={{ width: '100%' }}>
                <InputLabel id='label'>Date Format</InputLabel>
                <Select
                  labelId='label'
                  name={'date_format'}
                  value={value}
                  onChange={onChange}
                  label='Date Format'
                >
                  <MenuItem value={'M/d/yyyy'}>1/30/2022</MenuItem>
                  <MenuItem value={'d/M/yyyy'}>30/1/2022</MenuItem>
                  <MenuItem value={'yyyy/d/M'}>2022/1/30</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
        />
        <Controller
          name='alarm_popups'
          control={control}
          defaultValue={false}
          rules={{ required: false }}
          render={({ field: { onChange, value } }) => (
            <Grid item>
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={value} onChange={onChange} />}
                  label='Alarm Popups'
                />
              </FormGroup>
            </Grid>
          )}
        />
        <Controller
          name='currentPassword'
          control={control}
          defaultValue=''
          rules={{ required: false }}
          render={({ field: { onChange, value } }) => (
            <Grid item xs={12} style={{ marginRight: '1.2rem' }}>
              <TextField
                variant='outlined'
                required
                placeholder='Current Password'
                id='current-password'
                label='Current Password'
                name='currentPassword'
                fullWidth
                size='small'
                type='password'
                value={value}
                onChange={onChange}
              />
            </Grid>
          )}
        />
        <Grid
          container
          item
          xs={12}
          spacing={1}
          style={{ marginRight: '1.2rem' }}
        >
          <Controller
            name='newPassword'
            control={control}
            defaultValue=''
            rules={{
              validate: (value) => {
                return value
                  ? !watch('currentPassword')
                    ? 'Current password required to change password'
                    : true
                  : true;
              },
            }}
            render={({ field: { onChange, value } }) => (
              <Grid item xs={12}>
                <TextField
                  variant='outlined'
                  required
                  placeholder='New Password'
                  id='password'
                  fullWidth
                  label='New Password'
                  name='newPassword'
                  size='small'
                  type='password'
                  value={value}
                  onChange={onChange}
                />
              </Grid>
            )}
          />
          {watch('newPassword') && (
            <Grid item xs={12}>
              <Typography align='left' style={{ marginLeft: '1rem' }}>
                {`Password Strength: ${getPasswordStrength(
                  watch('newPassword')
                )}`}
              </Typography>
            </Grid>
          )}
        </Grid>
        <Grid
          item
          container
          style={{ marginBottom: '1.2rem', paddingLeft: 0 }}
          justifyContent='center'
        >
          <Grid item style={{ minWidth: '8rem' }}>
            <Button
              variant='contained'
              fullWidth
              color='primary'
              onClick={() => {
                handleSubmit(onSubmit)();
                reset({}, { keepValues: true });
              }}
              disabled={!isDirty}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <InfoPopup
        openCondition={isSubmitted && Object.keys(errors).length > 0}
        title='Form Error'
        message={
          errors.newPassword?.message
            ? (errors.newPassword.message as string)
            : ''
        }
        error
      />
    </Grid>
  );
};

export default UserSettingsPage;
